import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDays,
  faFilter,
  faArrowDown,
  faArrowUp,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { getUserPayments } from '../../../lib/api';
import '../../admin/pages/admin.css';
import './summary.css';

const PAGE_SIZE = 30;

const TYPE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
  { value: 'refund', label: 'Refund' },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

const Payments = () => {
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total_pages: 1, total_records: 0 });
  const [totals, setTotals] = useState({ credit: '0.00000', debit: '0.00000', refund: '0.00000' });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [transactionType, setTransactionType] = useState('');
  const [fromDate, setFromDate] = useState(() => isoDate(defaultFrom));
  const [toDate, setToDate] = useState(() => isoDate(today));
  const [appliedDates, setAppliedDates] = useState(null);

  const containerRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);

  const buildFilterParams = useCallback(
    (typeOverride, datesOverride) => {
      const type = typeOverride !== undefined ? typeOverride : transactionType;
      const dates = datesOverride !== undefined ? datesOverride : appliedDates;
      return {
        transactionType: type || undefined,
        fromDate: dates?.from || undefined,
        toDate: dates?.to || undefined,
      };
    },
    [transactionType, appliedDates]
  );

  const load = useCallback(async (nextPage, reset = false, typeOverride, datesOverride) => {
    if (loadingRef.current) return;
    if (!reset && !hasMoreRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError('');

    const filters = buildFilterParams(typeOverride, datesOverride);

    try {
      const resp = await getUserPayments({
        page: nextPage,
        limit: PAGE_SIZE,
        ...filters,
      });
      const list = resp?.data || resp?.payments || [];
      setRows((prev) => (reset ? list : [...prev, ...list]));
      setPagination(resp?.pagination || { total_pages: 1, total_records: list.length });
      if (resp?.totals) setTotals(resp.totals);

      const perPage = resp?.pagination?.per_page || PAGE_SIZE;
      const totalPages = resp?.pagination?.total_pages || 1;
      const noMore = nextPage >= totalPages || list.length < perPage;
      hasMoreRef.current = !noMore;

      if (reset) setIsInitialLoad(false);
      pageRef.current = nextPage;
    } catch (e) {
      setError(e?.message || 'Failed to load payments');
      if (reset) setRows([]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [buildFilterParams]);

  const resetAndLoad = useCallback(
    (typeOverride, datesOverride) => {
      pageRef.current = 1;
      hasMoreRef.current = true;
      setRows([]);
      setIsInitialLoad(true);
      load(1, true, typeOverride, datesOverride);
    },
    [load]
  );

  const onTypeChange = (nextType) => {
    setTransactionType(nextType);
    resetAndLoad(nextType, appliedDates);
  };

  const applyDateRange = () => {
    if (!fromDate || !toDate) {
      setError('Select both start and end dates.');
      return;
    }
    if (fromDate > toDate) {
      setError('Start date cannot be after end date.');
      return;
    }
    const range = { from: fromDate, to: toDate };
    setAppliedDates(range);
    setError('');
    resetAndLoad(transactionType, range);
  };

  const clearDateRange = () => {
    setAppliedDates(null);
    setFromDate(isoDate(defaultFrom));
    setToDate(isoDate(today));
    setError('');
    resetAndLoad(transactionType, null);
  };

  const onScroll = () => {
    const el = containerRef.current;
    if (!el || loadingRef.current || !hasMoreRef.current) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (nearBottom) load(pageRef.current + 1, false);
  };

  useEffect(() => {
    pageRef.current = 1;
    hasMoreRef.current = true;
    setRows([]);
    setIsInitialLoad(true);
    load(1, true, '', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTypeLabel =
    TYPE_FILTERS.find((t) => t.value === transactionType)?.label || 'All';

  const dateLabel =
    appliedDates?.from && appliedDates?.to
      ? `${appliedDates.from} to ${appliedDates.to}`
      : 'All dates';

  const totalRecords = pagination.total_records ?? rows.length;
  const hasRecords = !isInitialLoad && totalRecords > 0;

  const parseTotal = (v) => Number(v) || 0;

  const kpiItems = useMemo(() => {
    const items = [
      { key: 'credit', label: 'Credit', icon: faArrowUp, mod: 'credit', value: totals.credit },
      { key: 'debit', label: 'Debit', icon: faArrowDown, mod: 'debit', value: totals.debit },
      { key: 'refund', label: 'Refund', icon: faRotateLeft, mod: 'refund', value: totals.refund },
    ];
    return items.filter((item) => parseTotal(item.value) > 0);
  }, [totals]);

  return (
    <div className="payments-page">
      <header className="payments-page__hero">
        <span className="admin-page__eyebrow">Reports</span>
        <div className="payments-page__title-row">
          <h1 className="admin-page__title">Payments</h1>
          <p className="payments-page__meta">
            {loading && isInitialLoad
              ? 'Loading…'
              : `${pagination.total_records ?? rows.length} records · ${activeTypeLabel} · ${dateLabel}`}
          </p>
        </div>
      </header>

      <section className="admin-panel payments-panel">
        <div className="admin-panel__head">
          <h2>
            <FontAwesomeIcon icon={faFilter} className="payments-panel__head-icon" />
            Filters
          </h2>
          <p>Filter by transaction type and date range</p>
        </div>
        <div className="admin-panel__body payments-panel__body">
          <div className="payments-panel__section">
            <span className="admin-label">Transaction type</span>
            <div className="payments-segments" role="tablist" aria-label="Transaction type">
              {TYPE_FILTERS.map((opt) => (
                <button
                  key={opt.value || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={transactionType === opt.value}
                  className={`payments-segments__btn payments-segments__btn--${opt.value || 'all'} ${
                    transactionType === opt.value ? 'is-active' : ''
                  }`}
                  onClick={() => onTypeChange(opt.value)}
                  disabled={loading && isInitialLoad}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="payments-panel__section payments-panel__section--dates">
            <span className="admin-label">
              <FontAwesomeIcon icon={faCalendarDays} /> Date range
            </span>
            <div className="admin-grid admin-message-stats-range payments-date-grid">
              <div className="admin-field">
                <label className="admin-label" htmlFor="payments-from">
                  From
                </label>
                <input
                  id="payments-from"
                  type="date"
                  className="admin-input"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="admin-field">
                <label className="admin-label" htmlFor="payments-to">
                  To
                </label>
                <input
                  id="payments-to"
                  type="date"
                  className="admin-input"
                  value={toDate}
                  min={fromDate || undefined}
                  max={isoDate(today)}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="admin-field admin-field--actions payments-date-actions">
                <span className="admin-label" aria-hidden="true">
                  &nbsp;
                </span>
                <div className="payments-date-buttons">
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={applyDateRange}
                    disabled={loading}
                  >
                    Apply range
                  </button>
                  {appliedDates && (
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost"
                      onClick={clearDateRange}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faRotateLeft} />
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="admin-feedback admin-feedback--error payments-panel__error">
              {error}
            </div>
          )}
        </div>
      </section>

      {hasRecords && kpiItems.length > 0 && (
        <div
          className="payments-kpi-row"
          style={{ gridTemplateColumns: `repeat(${Math.min(kpiItems.length, 3)}, minmax(0, 1fr))` }}
        >
          {kpiItems.map((item) => (
            <div key={item.key} className={`payments-kpi payments-kpi--${item.mod}`}>
              <FontAwesomeIcon icon={item.icon} className="payments-kpi__icon" />
              <div>
                <span className="payments-kpi__label">{item.label}</span>
                <strong className="payments-kpi__value">₹{item.value}</strong>
              </div>
            </div>
          ))}
        </div>
      )}

      <section className="admin-panel payments-table-panel">
        <div className="admin-panel__head">
          <h2>Transaction history</h2>
          <p>
            {isInitialLoad && loading
              ? 'Loading…'
              : hasRecords
                ? `Showing ${rows.length} of ${totalRecords}`
                : 'No transactions for the selected filters'}
          </p>
        </div>
        <div ref={containerRef} className="summary-scroll payments-scroll" onScroll={onScroll}>
          <div className="summary-table-wrap">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Method</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Campaign ID</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {isInitialLoad && loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="skeleton-row">
                      <td><div className="sk sk-text" /></td>
                      <td><div className="sk sk-text" /></td>
                      <td><div className="sk sk-pill" /></td>
                      <td><div className="sk sk-text" /></td>
                      <td><div className="sk sk-text" /></td>
                      <td><div className="sk sk-text long" /></td>
                    </tr>
                  ))
                ) : rows.length ? (
                  rows.map((p, idx) => {
                    const type = String(p.transaction_type || '').toLowerCase();
                    return (
                      <tr key={p.payment_id ?? idx}>
                        <td>{p.payment_id || '—'}</td>
                        <td>{p.payment_method || '—'}</td>
                        <td>
                          <span className={`payments-type-badge payments-type-badge--${type || 'unknown'}`}>
                            {p.transaction_type || '—'}
                          </span>
                        </td>
                        <td className="payments-amount">₹{p.amount != null ? Number(p.amount).toFixed(5) : '—'}</td>
                        <td>
                          {p.campaign_id ? (
                            <Link
                              to={`/main/template-report?campaign_id=${p.campaign_id}`}
                              className="campaign-link"
                            >
                              {p.campaign_id}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{p.payment_date ? new Date(p.payment_date).toLocaleString() : '—'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="empty-cell">
                      No payments found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!isInitialLoad && loading && (
            <div className="summary-loading-bottom">
              <div className="loading-spinner-small" />
              <span>Loading more…</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Payments;
